<template>
    <v-container class="py-12">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Motoristas a pagar</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', to: '/home' },
                            { text: 'Financeiro', disabled: true },
                            { text: 'Motoristas a pagar', disabled: true }
                        ]" />
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- FILTRO -->
        <v-row>
            <v-col cols="12">
                <v-card class="filter-card">
                    <v-card-text>
                        <v-row dense align="center">
                            <v-col cols="12" md="2">
                                <v-text-field dense outlined hide-details type="date" label="De" v-model="filter.start" />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field dense outlined hide-details type="date" label="Até" v-model="filter.end" />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-select dense outlined hide-details label="Tipo" :items="typeOptions" v-model="filter.type" />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field dense outlined hide-details clearable label="Motorista"
                                    prepend-inner-icon="mdi-magnify" v-model="filter.name" @keyup.enter="load" />
                            </v-col>
                            <v-col cols="12" md="3" class="d-flex align-center" style="gap: 4px">
                                <v-btn color="primary" elevation="0" :loading="loading" @click="load">Filtrar</v-btn>
                                <v-btn small text color="primary" @click="setMonth(0)">Este mês</v-btn>
                                <v-btn small text color="primary" @click="setMonth(-1)">Mês passado</v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- INDICADORES -->
        <div class="stat-grid">
            <stat-card v-for="kpi in kpis" :key="kpi.key" :label="kpi.label" :value="kpi.value" :hint="kpi.hint" :color="kpi.color" />
        </div>

        <v-tabs v-model="tab" class="mt-4">
            <v-tab>Por motorista</v-tab>
            <v-tab @click="loadPayouts(1)">Fechamentos</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <!-- POR MOTORISTA -->
            <v-tab-item>
                <v-card outlined class="mt-3">
                    <v-data-table :headers="headers" :items="rows" :loading="loading" item-key="driver_id"
                        :items-per-page="50" class="app-table" no-data-text="Nada a pagar no período">
                        <template v-slot:[`item.name`]="{ item }">
                            <strong>{{ item.name }}</strong>
                            <div>
                                <v-chip x-small outlined :color="item.driver_type === 2 ? 'orange darken-2' : 'primary'">
                                    {{ item.driver_type === 2 ? 'Parceiro' : 'Funcionário' }}
                                </v-chip>
                                <span class="caption grey--text ml-1">{{ rule(item) }}</span>
                            </div>
                        </template>

                        <template v-slot:[`item.to_close`]="{ item }">
                            <template v-if="item.driver_type === 2">
                                <strong>{{ money(item.unclosed_amount) }}</strong>
                                <div class="caption grey--text">{{ item.unclosed_trips || 0 }} viagem(ns) fora de fechamento</div>
                                <div v-if="item.unclosed_pending" class="caption orange--text text--darken-2">
                                    {{ item.unclosed_pending }} sem preço
                                </div>
                            </template>
                            <template v-else>
                                <strong>{{ item.salary ? money(item.unclosed_amount) : '-' }}</strong>
                                <div class="caption grey--text">
                                    {{ item.salary ? `${item.months_without_payout} mês(es) sem fechamento` : 'Salário não cadastrado' }}
                                </div>
                            </template>
                        </template>

                        <template v-slot:[`item.payouts`]="{ item }">
                            <div class="payout-chips">
                                <v-chip v-for="payout in item.payouts" :key="payout.id" small outlined
                                    :color="statusColor(payout.status)" @click="openPayout(payout.id)">
                                    {{ shortPeriod(payout) }} · {{ money(payout.status === 'paid' ? payout.paid_amount : payout.total_amount) }}
                                    <span class="ml-1 font-weight-bold">{{ statusName(payout.status) }}</span>
                                </v-chip>
                                <span v-if="!item.payouts.length" class="caption grey--text">Nenhum</span>
                            </div>
                        </template>

                        <template v-slot:[`item.actions`]="{ item }">
                            <v-btn v-if="canManage" small outlined color="primary" @click="openGenerate(item)">
                                <v-icon left small>mdi-file-document-plus-outline</v-icon>Gerar fechamento
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-tab-item>

            <!-- FECHAMENTOS -->
            <v-tab-item>
                <v-card outlined class="mt-3">
                    <v-card-text class="pb-0">
                        <v-chip-group v-model="payoutStatus" @change="loadPayouts(1)">
                            <v-chip v-for="status in statusOptions" :key="status.value" :value="status.value" filter outlined small>
                                {{ status.text }}
                            </v-chip>
                        </v-chip-group>
                    </v-card-text>
                    <v-data-table :headers="payoutHeaders" :items="payouts.data" :loading="payouts.loading"
                        :server-items-length="payouts.total" :options.sync="payouts.options"
                        :footer-props="{ itemsPerPageOptions: [20] }" class="app-table hover-table"
                        no-data-text="Nenhum fechamento" @click:row="item => openPayout(item.id)"
                        @update:page="loadPayouts">
                        <template v-slot:[`item.driver`]="{ item }">
                            {{ item.driver ? (item.driver.nickname || item.driver.name) : '-' }}
                            <span class="caption grey--text">({{ item.driver_type === 2 ? 'parceiro' : 'funcionário' }})</span>
                        </template>
                        <template v-slot:[`item.period`]="{ item }">{{ date(item.period_start) }} a {{ date(item.period_end) }}</template>
                        <template v-slot:[`item.total_amount`]="{ item }">{{ money(item.total_amount) }}</template>
                        <template v-slot:[`item.status`]="{ item }">
                            <v-chip x-small dark :color="statusColor(item.status)">{{ statusName(item.status) }}</v-chip>
                            <span v-if="item.pending_count" class="caption orange--text text--darken-2 ml-1">{{ item.pending_count }} sem preço</span>
                        </template>
                        <template v-slot:[`item.paid_at`]="{ item }">{{ item.paid_at ? date(item.paid_at) : '-' }}</template>
                    </v-data-table>
                </v-card>
            </v-tab-item>
        </v-tabs-items>

        <!-- GERAR FECHAMENTO -->
        <v-dialog v-model="generate.open" max-width="520px">
            <v-card v-if="generate.driver">
                <v-card-title class="custom-modal-title text-uppercase black--text font-weight-bold">
                    Gerar fechamento
                </v-card-title>
                <v-card-text>
                    <p class="body-1 mb-1"><strong>{{ generate.driver.name }}</strong></p>
                    <p class="body-2 grey--text">
                        {{ generate.driver.driver_type === 2
                            ? 'Entram as viagens concluídas no período que ainda não estão em outro fechamento. O valor de cada viagem fica gravado.'
                            : 'Usa o salário vigente no fim do período. Lançamentos (vale, desconto, bônus) podem ser adicionados depois.' }}
                    </p>
                    <v-row dense>
                        <v-col cols="6">
                            <v-text-field dense outlined hide-details type="date" label="Início" v-model="generate.start" />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field dense outlined hide-details type="date" label="Fim" v-model="generate.end" />
                        </v-col>
                    </v-row>
                    <p class="caption grey--text mt-2 mb-0">Período sugerido conforme a periodicidade do motorista.</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="generate.open = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" :loading="generate.loading" @click="confirmGenerate">Gerar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const STATUS = {
    open: { text: 'Aberto', color: 'blue' },
    reviewed: { text: 'Conferido', color: 'purple' },
    paid: { text: 'Pago', color: 'green' },
    cancelled: { text: 'Cancelado', color: 'grey' }
}

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']

export default {
    name: 'DriverPayoutsOverview',

    data: () => ({
        loading: false,
        tab: 0,
        filter: {
            start: moment().startOf('month').format('YYYY-MM-DD'),
            end: moment().endOf('month').format('YYYY-MM-DD'),
            type: null,
            name: ''
        },
        typeOptions: [
            { value: null, text: 'Todos' },
            { value: 2, text: 'Parceiros' },
            { value: 1, text: 'Funcionários' }
        ],
        rows: [],
        totals: { to_close: 0, open: 0, paid: 0, pending_trips: 0 },
        headers: [
            { text: 'Motorista', value: 'name', sortable: false },
            { text: 'A fechar no período', value: 'to_close', sortable: false },
            { text: 'Fechamentos', value: 'payouts', sortable: false },
            { text: '', value: 'actions', sortable: false, align: 'right' }
        ],
        payoutStatus: null,
        statusOptions: Object.keys(STATUS).map(value => ({ value, text: STATUS[value].text })),
        payouts: { data: [], total: 0, loading: false, options: { page: 1, itemsPerPage: 20 } },
        payoutHeaders: [
            { text: 'Nº', value: 'id', sortable: false, width: 70 },
            { text: 'Motorista', value: 'driver', sortable: false },
            { text: 'Período', value: 'period', sortable: false },
            { text: 'Total', value: 'total_amount', sortable: false },
            { text: 'Situação', value: 'status', sortable: false },
            { text: 'Pago em', value: 'paid_at', sortable: false }
        ],
        generate: { open: false, loading: false, driver: null, start: null, end: null }
    }),

    computed: {
        canManage() {
            return this.$check(['financial.drivers.manage'])
        },
        kpis() {
            return [
                { key: 'to_close', label: 'A fechar', value: this.money(this.totals.to_close), color: '#1450b5', hint: 'Viagens e salários ainda sem fechamento' },
                { key: 'open', label: 'Fechado, a pagar', value: this.money(this.totals.open), color: '#6a1b9a', hint: 'Fechamentos abertos ou conferidos' },
                { key: 'paid', label: 'Pago', value: this.money(this.totals.paid), color: '#2e7d32', hint: 'Fechamentos pagos no período' },
                { key: 'pending', label: 'Viagens sem preço', value: this.totals.pending_trips, color: this.totals.pending_trips ? '#e65100' : '#5a6b85', hint: 'Parceiro sem valor para a localidade' }
            ]
        }
    },

    created() {
        this.load()
    },

    methods: {
        setMonth(offset) {
            const month = moment().add(offset, 'month')
            this.filter.start = month.clone().startOf('month').format('YYYY-MM-DD')
            this.filter.end = month.clone().endOf('month').format('YYYY-MM-DD')
            this.load()
        },

        async load() {
            this.loading = true
            try {
                const response = await axios.get('/api/v2/financial/driver-payouts/overview', {
                    params: {
                        start: this.filter.start,
                        end: this.filter.end,
                        type: this.filter.type || undefined,
                        name: this.filter.name || undefined
                    }
                })
                const content = response.data.content
                this.rows = content.rows || []
                this.totals = content.totals || this.totals
                if (this.tab === 1) this.loadPayouts(1)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.loading = false
            }
        },

        async loadPayouts(page = 1) {
            this.payouts.loading = true
            try {
                const response = await axios.get('/api/v2/financial/driver-payouts', {
                    params: {
                        page,
                        limit: 20,
                        status: this.payoutStatus || undefined,
                        type: this.filter.type || undefined,
                        start: this.filter.start,
                        end: this.filter.end
                    }
                })
                const content = response.data.content
                this.payouts.data = content.data || []
                this.payouts.total = content.total || 0
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.payouts.loading = false
            }
        },

        async openGenerate(row) {
            this.generate = { open: true, loading: false, driver: row, start: this.filter.start, end: this.filter.end }

            try {
                const response = await axios.get(`/api/v2/financial/drivers/${row.driver_id}/suggest-period`, {
                    params: { date: moment().isAfter(moment(this.filter.end)) ? this.filter.end : moment().format('YYYY-MM-DD') }
                })
                this.generate.start = response.data.content.start
                this.generate.end = response.data.content.end
            } catch (error) {
                // Mantém o período do filtro
            }
        },

        async confirmGenerate() {
            this.generate.loading = true
            try {
                const response = await axios.post('/api/v2/financial/driver-payouts', {
                    driver_id: this.generate.driver.driver_id,
                    period_start: this.generate.start,
                    period_end: this.generate.end
                })
                this.generate.open = false
                this.$dialog.notify.success('Fechamento gerado', { position: 'top-right', timeout: 3000 })
                this.openPayout(response.data.content.id)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 })
            } finally {
                this.generate.loading = false
            }
        },

        openPayout(id) {
            this.$router.push(`/financeiro/motoristas/fechamentos/${id}`)
        },

        rule(item) {
            if (item.driver_type === 1) return item.salary ? `Salário ${this.money(item.salary)}` : ''
            const settings = item.settings || {}
            if (settings.period_type === 'weekly') return `Semanal, fecha ${WEEKDAYS[settings.closing_weekday] || 'sexta'}`
            if (settings.period_type === 'biweekly') return 'Quinzenal'
            return settings.closing_day ? `Mensal, fecha dia ${settings.closing_day}` : 'Mensal'
        },

        shortPeriod(payout) {
            return `${moment(payout.period_start).format('DD/MM')}–${moment(payout.period_end).format('DD/MM')}`
        },

        statusName(status) {
            return (STATUS[status] || {}).text || status
        },

        statusColor(status) {
            return (STATUS[status] || {}).color || 'grey'
        },

        money(value) {
            return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        date(value) {
            return value ? moment.utc(value).format('DD/MM/YYYY') : '-'
        }
    }
}
</script>

<style lang="scss" scoped>
.payout-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 0;
}
</style>
